import {
  BIG_CARDS_MIN_RANK,
  BRIDGE_SEATS,
  type BridgeCard,
  type BridgeSeat,
  type PirateGame,
  type PirateRound,
  type PirateRoundResult,
  SINGLE_PLAYER,
  newPirateGame,
  newPirateRound,
  nextRoundSpec,
  pirateChooseCard,
  pirateChooseGuess,
  pirateLadder,
  pirateRoundResult,
  pirateViewFor,
  pirateWinners,
  piratePlayCard,
  playHiddenCard,
  recordRound,
  seededRandom,
  submitGuess,
  newTournament,
  parseTournament,
  recordTournamentGame,
  serializeTournament,
  tournamentStatus,
  type Tournament,
} from '@chessmate/game-engine';
import { create } from 'zustand';
import { rememberBigCards, resolveBigCards } from '../lib/big-cards.js';
import type { AppQuery } from '../lib/query.js';
import { playSfx, setAmbientMood } from '../services/audio.js';
import { cueCardPlay, cueDeal, cueRoundOrGame, cueSweep, cueTurn } from './sfx-cues.js';
import { CHAT_BRIDGE, chatGameEnd, chatRound, chatTrick, chatterNewGame, extremeSeat, robotsOf } from './table-chatter.js';

/**
 * The Pirate Bridge store (single device: you in South, three robots, everyone for themselves). Its own store, like Whist's, because
 * nothing here is FEN-shaped. Robots decide from `pirateViewFor` only, so they never see a hand or a guess they may not.
 *
 * A game is a ladder of rounds (13..1..13 cards, or 7..1..7 with Big Cards). Each round everyone guesses how many tricks they will
 * win, all at once; an exact guess scores 10 + the tricks, a miss scores 0. With Cap'n's Hat on, the 1-card round is played with the
 * card on your forehead: you see everyone else's card, never your own. Rules: docs/proposals/whist-and-pirate-bridge.md.
 */
export const HUMAN_SEAT: BridgeSeat = 'S';

/** Pause before a robot plays, so a kid can follow the trick. Zero in tests and `?engine=fake`. */
const ROBOT_DELAY_MS = 650;

export type PirateStrength = 'easy' | 'clever';

export interface PirateState {
  game: PirateGame | null;
  round: PirateRound | null;
  /** The finished round's result, until the next round starts. */
  result: PirateRoundResult | null;
  /** Rounds dealt so far in this game, counting the current one (0 = no game). */
  roundNumber: number;
  /** Set when the ladder is finished: everyone with the top total (a tie shares the win). */
  winners: BridgeSeat[] | null;
  /** The guess the human has dialled in but not locked yet. */
  draftGuess: number;
  strength: PirateStrength;
  fourColour: boolean;
  /** Big Cards: the 28-card deck of 8 to Ace, ladder 7..1..7. Remembered per device. */
  bigCards: boolean;
  /** Cap'n's Hat: the 1-card round is played with the card on your forehead. Off by default. */
  hat: boolean;
  /** Race to a target (100 or 200) instead of the full ladder. 0 = the full ladder. A table option, not remembered. */
  raceTarget: 0 | 100 | 200;
  /** The weekend tournament (session -> series), persisted on the device. null = none running. */
  tournament: Tournament<BridgeSeat> | null;
  /** Rival seat names for the tournament standings, set before the first game. South is always you. */
  seatNames: Partial<Record<RivalSeat, string>>;
  seed: number | null;
  query: AppQuery | null;
  start(query: AppQuery, seed?: number): void;
  nextRound(): void;
  newGame(): void;
  stop(): void;
  setDraftGuess(n: number): void;
  /** Lock in the dialled guess; the robots have already guessed, so play begins. */
  lockGuess(): void;
  play(card: BridgeCard): void;
  /** Cap'n's Hat: play the card on your forehead (you never see it, so there is nothing to choose). */
  playHat(): void;
  setStrength(strength: PirateStrength): void;
  setFourColour(on: boolean): void;
  setBigCards(on: boolean): void;
  setHat(on: boolean): void;
  setRaceTarget(target: 0 | 100 | 200): void;
  /** Archive the tournament: cleared from the device, standings gone. */
  endTournament(): void;
  setSeatNames(names: Partial<Record<RivalSeat, string>>): void;
}

const TOURNAMENT_KEY = 's4k:pirate-tournament';
const SEAT_NAMES_KEY = 's4k:pirate-seat-names';
export type RivalSeat = 'N' | 'E' | 'W';
const loadSeatNames = (): Partial<Record<RivalSeat, string>> => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = JSON.parse(window.localStorage.getItem(SEAT_NAMES_KEY) ?? '{}') as Record<string, unknown>;
    const clean: Partial<Record<RivalSeat, string>> = {};
    for (const seat of ['N', 'E', 'W'] as const) {
      const v = raw[seat];
      if (typeof v === 'string' && v.trim()) clean[seat] = v.trim().slice(0, 20);
    }
    return clean;
  } catch {
    return {};
  }
};
const saveSeatNames = (names: Partial<Record<RivalSeat, string>>): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SEAT_NAMES_KEY, JSON.stringify(names));
  } catch {
    // best effort
  }
};
const loadTournament = (): Tournament<BridgeSeat> | null => {
  if (typeof window === 'undefined') return null;
  try {
    return parseTournament(window.localStorage.getItem(TOURNAMENT_KEY), [...BRIDGE_SEATS]);
  } catch {
    return null;
  }
};
/** Read-only summary for surfaces that should not subscribe to the store (Home's continue banner). */
export function pirateTournamentSummary(): { sessionWins: number; seriesWins: number; games: number } | null {
  const t = loadTournament();
  if (!t) return null;
  return { sessionWins: t.session.wins.S, seriesWins: t.series.wins.S, games: t.session.games };
}

const saveTournament = (t: Tournament<BridgeSeat> | null): void => {
  if (typeof window === 'undefined') return;
  try {
    if (t === null) window.localStorage.removeItem(TOURNAMENT_KEY);
    else window.localStorage.setItem(TOURNAMENT_KEY, serializeTournament(t));
  } catch {
    // A full or blocked storage loses the tournament, not the game.
  }
};

let timer: ReturnType<typeof setTimeout> | null = null;
const clearTimer = () => {
  if (timer) clearTimeout(timer);
  timer = null;
};

const freshSeed = () => Math.floor(Math.random() * 2 ** 31);
/** Each game has one seed; its rounds derive their own in the engine (`nextRoundSpec`), so a seeded game replays exactly. */

export const usePirateStore = create<PirateState>((set, get) => {
  const minRank = () => (get().bigCards ? BIG_CARDS_MIN_RANK : 2);

  /** Take a new round state; when it ends the round, score it, and when that ends the ladder, say who won. */
  const advance = (round: PirateRound) => {
    const { round: before } = get();
    // Seat panning (spec P2): North and South sit centre, East right, West left. Guesses are not cards.
    if (before?.phase === 'play') {
      cueCardPlay(before.turn);
      cueSweep(before, round);
      chatTrick(before, round, robotsOf(BRIDGE_SEATS), CHAT_BRIDGE);
    }
    const result = pirateRoundResult(round);
    const { game } = get();
    if (!result || !game) {
      set({ round });
      cueTurn(before?.phase === 'play' ? before.turn : null, round.phase === 'play' ? round.turn : null, HUMAN_SEAT);
      return;
    }
    const next = recordRound(game, result);
    const winners = pirateWinners(next);
    // The game-ending round gets win/lose; any other round gets the stinger (exact guess = won).
    cueRoundOrGame(winners ? (winners.includes(HUMAN_SEAT) ? 'won' : 'lost') : result[HUMAN_SEAT].exact ? 'won' : 'lost', winners !== null);
    // Table talk from public facts: each seat's exact-guess score, and the winners of a finished race.
    const scores = Object.fromEntries(BRIDGE_SEATS.map((s) => [s, result[s].score]));
    if (winners) chatGameEnd(winners, robotsOf(BRIDGE_SEATS), CHAT_BRIDGE);
    else chatRound(extremeSeat(scores, robotsOf(BRIDGE_SEATS), 'best'), extremeSeat(scores, robotsOf(BRIDGE_SEATS), 'worst'), CHAT_BRIDGE);
    let tournament = get().tournament;
    if (winners && tournament && tournamentStatus(tournament).state === 'playing') {
      tournament = recordTournamentGame(tournament, winners);
      saveTournament(tournament);
    }
    set({ round, result, game: next, winners, tournament });
  };

  /** If a robot is to play, let it, after a short pause; stops at the human's turn or the end of the round. */
  const driveRobots = () => {
    clearTimer();
    const { round, query, strength } = get();
    if (!round || round.phase !== 'play' || round.controllers[round.turn] !== 'ai') return;
    const delay = query?.engine === 'fake' ? 0 : ROBOT_DELAY_MS;
    timer = setTimeout(() => {
      const current = get().round;
      if (!current || current.phase !== 'play' || current.controllers[current.turn] !== 'ai') return;
      if (current.hat) advance(playHiddenCard(current));
      else {
        const random = seededRandom(current.seed + current.tricks.length * 4 + current.trick.length);
        advance(piratePlayCard(current, pirateChooseCard(pirateViewFor(current, current.turn), strength, random)));
      }
      driveRobots();
    }, delay);
  };

  const deal = () => {
    clearTimer();
    const { game, strength } = get();
    const spec = game ? nextRoundSpec(game) : null;
    if (!game || !spec) return;
    let round = newPirateRound(spec);
    // The robots guess at once, in secret; the human's guess is the only one still missing.
    const random = seededRandom(spec.seed ^ 0x9e3779b1);
    for (const seat of BRIDGE_SEATS) {
      if (seat !== HUMAN_SEAT) round = submitGuess(round, seat, pirateChooseGuess(pirateViewFor(round, seat), strength, random));
    }
    set({ round, result: null, roundNumber: spec.index + 1, draftGuess: 0 });
    cueDeal();
    cueTurn(null, HUMAN_SEAT, HUMAN_SEAT);
    cueDeal();
    cueTurn(null, HUMAN_SEAT, HUMAN_SEAT);
  };

  const begin = (query: AppQuery, seed: number) => {
    clearTimer();
    chatterNewGame();
    const race = get().raceTarget;
    const game = newPirateGame({
      seed,
      controllers: SINGLE_PLAYER,
      minRank: minRank(),
      hat: get().hat,
      mode: race ? { kind: 'target', target: race } : { kind: 'ladder' },
    });
    set({ game, seed, query, winners: null, result: null, round: null, roundNumber: 0 });
    deal();
  };

  return {
    game: null,
    round: null,
    result: null,
    roundNumber: 0,
    winners: null,
    draftGuess: 0,
    strength: 'easy',
    fourColour: false,
    bigCards: false,
    hat: false,
    raceTarget: 0,
    tournament: loadTournament(),
    seatNames: loadSeatNames(),
    seed: null,
    query: null,
    start: (query, seed) => {
      setAmbientMood('cards');
      set({ bigCards: resolveBigCards('pirate', query.bigCards) });
      begin(query, seed ?? query.deal ?? freshSeed());
    },
    nextRound: () => {
      const { result, winners } = get();
      if (!result || winners) return;
      deal();
    },
    newGame: () => {
      const { query } = get();
      if (query) begin(query, query.deal ?? freshSeed());
    },
    stop: () => {
      clearTimer();
      set({ game: null, round: null, result: null, seed: null, roundNumber: 0, winners: null, draftGuess: 0 });
    },
    setDraftGuess: (n) => {
      const { round } = get();
      if (!round || round.phase !== 'guess') return;
      set({ draftGuess: Math.max(0, Math.min(round.cardsPerHand, Math.round(n))) });
    },
    lockGuess: () => {
      const { round, draftGuess } = get();
      if (!round || round.phase !== 'guess' || round.guesses[HUMAN_SEAT] !== undefined) return;
      playSfx('guess-lock');
      advance(submitGuess(round, HUMAN_SEAT, draftGuess));
      driveRobots();
    },
    play: (card) => {
      const { round } = get();
      if (!round || round.phase !== 'play' || round.turn !== HUMAN_SEAT || round.hat) return;
      if (!pirateViewFor(round, HUMAN_SEAT).legalPlays.some((c) => c.suit === card.suit && c.rank === card.rank)) return;
      advance(piratePlayCard(round, card));
      driveRobots();
    },
    playHat: () => {
      const { round } = get();
      if (!round || round.phase !== 'play' || round.turn !== HUMAN_SEAT || !round.hat) return;
      advance(playHiddenCard(round));
      driveRobots();
    },
    setStrength: (strength) => set({ strength }),
    setFourColour: (fourColour) => set({ fourColour }),
    setBigCards: (bigCards) => {
      rememberBigCards('pirate', bigCards);
      set({ bigCards });
      const { query, seed } = get();
      if (query && seed !== null) begin(query, seed);
    },
    setHat: (hat) => {
      set({ hat });
      const { query, seed } = get();
      if (query && seed !== null) begin(query, seed);
    },
    setRaceTarget: (raceTarget) => {
      set({ raceTarget });
      const { query, seed } = get();
      if (query && seed !== null) begin(query, seed);
    },
    endTournament: () => {
      saveTournament(null);
      set({ tournament: null });
    },
    setSeatNames: (names) => {
      const clean: Partial<Record<RivalSeat, string>> = {};
      for (const seat of ['N', 'E', 'W'] as const) {
        const v = names[seat]?.trim().slice(0, 20);
        if (v) clean[seat] = v;
      }
      saveSeatNames(clean);
      set({ seatNames: clean });
    },
  };
});

export { pirateLadder };
